import React, {
  useContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
  useId,
} from "react";
import { createPortal } from "react-dom";

import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { PlanningContext } from "../../../context/domain/planning/PlanningContext";
import { formatDate } from "../../../utils/datetime";
import {
  getUniqueWorkerTypeLabelsFromWorkers,
  workerMatchesWorkerTypeLabel,
} from "../../../utils/business/worker";
import useOutsideClick from "../../../hooks/useOutsideClick";

import Button from "../../../components/ui/buttons/Button";
import WorkerMultiSelect from "../../../components/ui/workers/WorkerMultiSelect";

import { IoClose } from "react-icons/io5";

/**
 * Full-screen modal for assigning workers to a dispatch scope or schedule crew.
 * Renders a searchable checkbox list with optional grouping by worker type.
 * Worker catalog defaults to {@link DispatchContext}, then {@link PlanningContext}.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen Whether the modal is open.
 * @param {Function} props.onClose Called when the user closes without submitting.
 * @param {Function} props.onSubmit `(selectedWorkerIds: number[]) => void` on submit.
 * @param {Date} props.date Dispatch/planning date shown in the header.
 * @param {Object} props.scope Scope (and job) context for the header.
 * @param {Array<Object>} props.usedWorkers Workers disabled because already assigned elsewhere.
 * @param {Array<number>} props.selectedWorkerIds Initially checked worker IDs.
 * @param {boolean} [props.byCategory=false] Group checkboxes by worker type.
 * @param {Array<Object>} [props.workers] Override worker list; omit to use context.
 * @param {string} [props.overlayZIndexClassName='z-[10060]'] Tailwind z-index for the overlay.
 * @param {import('react').ReactNode|((selectedWorkerIds: number[]) => import('react').ReactNode)} [props.headerContent] Slot below the title row.
 * @param {import('react').ReactNode|((selectedWorkerIds: number[]) => import('react').ReactNode)} [props.footerContent] Slot above the submit button.
 * @returns {JSX.Element|null}
 */
const WorkerSelectModal = ({
  isOpen,
  onClose,
  onSubmit,
  date,
  scope,
  usedWorkers,
  selectedWorkerIds,
  byCategory = false,
  showRoles = true,
  workers: workersProp,
  overlayZIndexClassName = "z-[10060]",
  headerContent = null,
  footerContent = null,
}) => {
  const dispatchContext = useContext(DispatchContext);
  const planningContext = useContext(PlanningContext);
  // Callers often pass `job?.workers || []`; an empty array is not "crew loaded" — it
  // blocked falling through to Dispatch/Planning `workers` (regression: blank modal).
  const hasExplicitNonEmptyCrew =
    Array.isArray(workersProp) && workersProp.length > 0;
  const workers = hasExplicitNonEmptyCrew
    ? workersProp
    : dispatchContext?.workers ?? planningContext?.workers;
  const [currentSelection, setCurrentSelection] = useState(
    new Set(selectedWorkerIds)
  );
  const [typeFilter, setTypeFilter] = useState("");
  const typeSelectId = useId();
  const modalRef = useRef(null);
  useOutsideClick(modalRef, () => onClose());

  // Workers that are in usedWorkers but not currently selected are disabled (cannot be selected)
  /**
   * Workers already used elsewhere today and not in the current selection (cannot be added).
   *
   * @param {void}
   * @returns {Array<number>}
   */
  const disabledWorkerIds = useMemo(() => {
    if (!workers || !usedWorkers) return [];
    return workers
      .filter(
        (w) =>
          usedWorkers.some((u) => u.id === w.id) &&
          !selectedWorkerIds.includes(w.id)
      )
      .map((w) => w.id);
  }, [workers, usedWorkers, selectedWorkerIds]);

  /**
   * Set form of {@link disabledWorkerIds} for O(1) select-all filtering.
   *
   * @param {void}
   * @returns {Set<number>}
   */
  const disabledWorkerIdSet = useMemo(
    () => new Set(disabledWorkerIds),
    [disabledWorkerIds]
  );

  /**
   * Unique worker-type labels for the type filter dropdown.
   *
   * @param {void}
   * @returns {Array<string>}
   */
  const workerTypeOptions = useMemo(
    () => getUniqueWorkerTypeLabelsFromWorkers(workers ?? []),
    [workers]
  );

  useEffect(() => {
    if (typeFilter && !workerTypeOptions.includes(typeFilter)) {
      setTypeFilter("");
    }
  }, [typeFilter, workerTypeOptions]);

  /**
   * Clear the in-modal selection without submitting.
   *
   * @param {import('react').MouseEvent} e Click event.
   * @returns {void}
   */
  const handleClear = useCallback((e) => {
    e.preventDefault();
    setCurrentSelection(new Set());
  }, []);

  const linkButtonClass =
    "text-sm text-green-900 disabled:text-gray-600 hover:text-green-900 transition-all ease-in-out disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-green-600";

  /**
   * Add every enabled worker of the current type filter to the selection.
   *
   * @param {import('react').MouseEvent} e Click event.
   * @returns {void}
   */
  const handleSelectAllByType = useCallback(
    (e) => {
      e.preventDefault();
      if (!typeFilter || !workers?.length) {
        return;
      }
      setCurrentSelection((prev) => {
        const next = new Set(prev);
        for (const w of workers) {
          if (
            workerMatchesWorkerTypeLabel(w, typeFilter) &&
            !disabledWorkerIdSet.has(w.id)
          ) {
            next.add(w.id);
          }
        }
        return next;
      });
    },
    [typeFilter, workers, disabledWorkerIdSet]
  );

  /**
   * Remove every worker of the current type filter from the selection.
   *
   * @param {import('react').MouseEvent} e Click event.
   * @returns {void}
   */
  const handleRemoveAllByType = useCallback(
    (e) => {
      e.preventDefault();
      if (!typeFilter || !workers?.length) {
        return;
      }
      const removeIds = new Set(
        workers
          .filter((w) => workerMatchesWorkerTypeLabel(w, typeFilter))
          .map((w) => w.id)
      );
      setCurrentSelection(
        (prev) => new Set([...prev].filter((id) => !removeIds.has(id)))
      );
    },
    [typeFilter, workers]
  );

  /**
   * Replace the in-modal selection from the multi-select control.
   *
   * @param {Array<number>} ids Next selected worker ids.
   * @returns {void}
   */
  const handleSelectionChange = useCallback((ids) => {
    setCurrentSelection(new Set(ids));
  }, []);

  /**
   * Submit the current selection to the parent (scope or schedule crew).
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const handleSubmit = useCallback(async () => {
    const ids = Array.from(currentSelection);
    try {
      onSubmit(ids);
    } catch (error) {
      console.error("Failed to update workers from modal", error);
    }
  }, [currentSelection, onSubmit]);

  useEffect(() => {
    if (isOpen) {
      setTypeFilter("");
    }
    if (isOpen && selectedWorkerIds) {
      setCurrentSelection(new Set(selectedWorkerIds));
    }
  }, [isOpen, selectedWorkerIds]);

  if (!isOpen) return null;

  if (typeof document === "undefined") {
    return null;
  }

  if (!workers) {
    return createPortal(
      <div
        data-schedule-worker-select-modal=""
        className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 ${overlayZIndexClassName}`}
      >
        <div className="bg-white p-4 w-auto rounded-md shadow-lg">
          <p>Loading workers...</p>
        </div>
      </div>,
      document.body
    );
  }

  const selectedIdsArray = Array.from(currentSelection);
  const renderedHeaderSlot =
    typeof headerContent === "function"
      ? headerContent(selectedIdsArray)
      : headerContent;
  const renderedFooterSlot =
    typeof footerContent === "function"
      ? footerContent(selectedIdsArray)
      : footerContent;

  return createPortal(
    <div
      data-schedule-worker-select-modal=""
      className={`fixed inset-0 flex font-inter items-center justify-center bg-black bg-opacity-50 p-4 ${overlayZIndexClassName}`}
    >
      <div
        className="bg-white p-4 w-full max-w-[min(100vw-2rem,56rem)] rounded-md shadow-lg flex flex-col max-h-[calc(100vh-2rem)] min-h-0 overflow-hidden"
        ref={modalRef}
      >
        <div className="flex flex-row justify-between shrink-0">
          <h1 className="text-[1rem] text-black max-w-[512px]">
            Select <b>{scope ? scope.scope?.title : "Off Work"}</b> workers for{" "}
            <b>
              {scope ? `${scope.job.number} - ${scope.job.description} ` : null}
            </b>
            {scope ? "on" : null}{" "}
            <b>{formatDate(date, "weekday MM/DD/YY")}</b>
          </h1>
          <div className="text-gray-400 ml-4">
            <button
              type="button"
              onClick={onClose}
              className="ml-2 text-2xl text-gray-500"
            >
              <IoClose />
            </button>
          </div>
        </div>

        {renderedHeaderSlot ? (
          <div className="my-1 w-full shrink-0 border-b border-gray-100">
            {renderedHeaderSlot}
          </div>
        ) : null}

        <div
          className={`${
            renderedHeaderSlot ? "mt-2" : "mt-3"
          } mb-2 flex w-full shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2`}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <label htmlFor={typeSelectId} className="sr-only">
              Select by worker type
            </label>
            <select
              id={typeSelectId}
              className="max-w-[min(100%,14rem)] min-w-[9rem] shrink-0 rounded-md border text-xs border-gray-300 bg-white px-2 py-0.5 text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/40"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">Select by type…</option>
              {workerTypeOptions.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={linkButtonClass}
              disabled={!typeFilter}
              onClick={handleSelectAllByType}
            >
              Select All
            </button>
            <button
              type="button"
              className={linkButtonClass}
              disabled={!typeFilter}
              onClick={handleRemoveAllByType}
            >
              Remove All
            </button>
          </div>
          <div className="shrink-0 text-right">
            <button
              type="button"
              onClick={handleClear}
              className={linkButtonClass}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2 shadow-md">
          <WorkerMultiSelect
            workers={workers}
            selectedWorkerIds={Array.from(currentSelection)}
            onChange={handleSelectionChange}
            disabledWorkerIds={disabledWorkerIds}
            byCategory={byCategory}
            showRoles={showRoles}
            multiCol
            maxColumns={2}
          />
        </div>

        {renderedFooterSlot ? (
          <div className="w-full shrink-0 border-t border-gray-100 pt-3">
            {renderedFooterSlot}
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center gap-2 shrink-0">
          <Button
            type="button"
            onClick={handleSubmit}
            variant="secondary"
            text="Submit"
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WorkerSelectModal;
